<template>
  <section class="setup-bg">
    <div class="setup-card">
      <div class="card-accent-bar"></div>
      <div class="card-inner">

        <!-- Logo -->
        <div class="setup-logo-row">
          <router-link to="/home" class="logo-link">
            <div class="logo-ring">
              <img src="@/assets/images/logo.png" alt="Buzz Booking" height="52" />
            </div>
          </router-link>
          <h2 class="brand-name">Buzz Booking</h2>
          <span class="brand-badge">Account Setup</span>
        </div>

        <div class="setup-intro">
          <p>Almost there! Choose a booking address — this is the unique slug customers will use to reach your booking page.</p>
        </div>

        <form @submit.prevent="save">
          <div class="field">
            <label class="field-label">Booking Address <span class="field-required">*</span></label>
            <div class="slug-wrap">
              <span class="slug-prefix">{{ origin }}/book/</span>
              <input
                v-model="slug"
                type="text"
                class="slug-input"
                :class="{ 'slug-input--error': slugError, 'slug-input--ok': slugAvailable === true }"
                placeholder="my-business"
                autocomplete="off"
                spellcheck="false"
                @input="onSlugInput"
                @blur="checkAvailability"
              />
            </div>
            <div v-if="slugError" class="field-error">{{ slugError }}</div>
            <div v-else-if="slugAvailable === true" class="field-ok">
              <i class="pi pi-check-circle"></i> Available
            </div>
            <div v-else-if="slugAvailable === false" class="field-error">
              <i class="pi pi-times-circle"></i> This address is already taken
            </div>
            <div class="field-hint">Lowercase letters, numbers, and hyphens only. No spaces.</div>
          </div>

          <div v-if="apiError" class="setup-error">{{ apiError }}</div>

          <button class="btn-save" type="submit" :disabled="saving || !slug">
            <span v-if="saving"><i class="pi pi-spin pi-spinner"></i> Saving…</span>
            <span v-else>Finish Setup <i class="pi pi-arrow-right"></i></span>
          </button>
        </form>

        <div class="setup-footer">
          <button class="btn-signout" type="button" @click="signOut">
            <i class="pi pi-sign-out"></i> Sign out
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import http from '@/lib/http'
import { useSessionStore } from '@/stores/session'

const router = useRouter()
const session = useSessionStore()

const slug = ref('')
const slugError = ref('')
const slugAvailable = ref(null)  // null = unchecked, true = available, false = taken
const saving = ref(false)
const apiError = ref('')

const origin = computed(() => {
  try { return window.location.origin } catch { return '' }
})

function slugify(val) {
  return val.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-{2,}/g, '-').replace(/^-|-$/g, '')
}

function validateSlugFormat(val) {
  if (!val) return 'Booking address is required.'
  if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(val) && !/^[a-z0-9]$/.test(val)) {
    return 'Use lowercase letters, numbers, and hyphens only. Cannot start or end with a hyphen.'
  }
  if (val.length < 3) return 'Must be at least 3 characters.'
  if (val.length > 60) return 'Must be 60 characters or fewer.'
  return ''
}

function onSlugInput(e) {
  slug.value = slugify(e.target.value)
  slugError.value = ''
  slugAvailable.value = null
}

async function checkAvailability() {
  const val = slug.value
  const err = validateSlugFormat(val)
  if (err) { slugError.value = err; slugAvailable.value = null; return }
  try {
    const res = await http.get('/business/check-booking-address', { params: { bookingAddress: val } })
    slugAvailable.value = res.data?.available === true
    if (slugAvailable.value === false) {
      slugError.value = 'This address is already taken.'
    }
  } catch {
    slugAvailable.value = null
  }
}

async function save() {
  apiError.value = ''
  const val = slug.value
  const err = validateSlugFormat(val)
  if (err) { slugError.value = err; return }

  // Re-check availability if not yet confirmed
  if (slugAvailable.value !== true) {
    await checkAvailability()
    if (slugAvailable.value !== true) return
  }

  saving.value = true
  try {
    await http.patch('/business/', { bookingAddress: val })
    session.setHasAccount(true)
    router.push({ name: 'admin-dashboard' })
  } catch (err) {
    apiError.value = err?.response?.data?.message || 'Failed to save. Please try again.'
  } finally {
    saving.value = false
  }
}

function signOut() {
  session.clear()
  router.push({ name: 'login' })
}
</script>

<style scoped>
.setup-bg {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-color: #363a48;
  background-image:
    repeating-linear-gradient(45deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 22px),
    repeating-linear-gradient(-45deg, rgba(255,255,255,0.06) 0px, rgba(255,255,255,0.06) 1px, transparent 1px, transparent 22px);
}

.setup-bg::before {
  content: '';
  position: fixed;
  inset: 0;
  background: radial-gradient(ellipse 80% 80% at 50% 50%, transparent 40%, rgba(0,0,0,0.45) 100%);
  pointer-events: none;
}

.setup-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 460px;
  background: #fff;
  border-radius: 1.375rem;
  overflow: hidden;
  box-shadow: 0 40px 90px rgba(0,0,0,0.55), 0 10px 28px rgba(0,0,0,0.3), 0 0 0 1px rgba(239,203,1,0.13);
}

.card-accent-bar {
  height: 5px;
  background: linear-gradient(90deg, #b89600 0%, #efcb01 35%, #fff38c 65%, #efcb01 100%);
}

.card-inner {
  padding: 2rem 2.25rem;
}

/* Logo */
.setup-logo-row {
  text-align: center;
  margin-bottom: 1.5rem;
}

.logo-link { display: inline-block; text-decoration: none; }

.logo-ring {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 78px;
  height: 78px;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 40%, rgba(239,203,1,0.13) 0%, rgba(239,203,1,0.04) 60%, transparent 100%);
  border: 2px solid rgba(239,203,1,0.28);
  margin-bottom: 0.5rem;
}

.brand-name {
  font-size: 1.4rem;
  font-weight: 700;
  color: #18100a;
  margin: 0 0 0.3rem;
}

.brand-badge {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #9a8555;
  background: rgba(239,203,1,0.1);
  border: 1px solid rgba(239,203,1,0.22);
  border-radius: 999px;
  padding: 0.15rem 0.75rem;
}

.setup-intro {
  margin-bottom: 1.5rem;
}

.setup-intro p {
  font-size: 0.88rem;
  color: #475569;
  line-height: 1.6;
  margin: 0;
  text-align: center;
}

/* Field */
.field { margin-bottom: 1.25rem; }

.field-label {
  display: block;
  font-size: 0.76rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}

.field-required { color: #b91c1c; }

.slug-wrap {
  display: flex;
  align-items: stretch;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  overflow: hidden;
  background: #f8fafc;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.slug-wrap:focus-within {
  border-color: #243654;
  box-shadow: 0 0 0 3px rgba(36,54,84,0.09);
}

.slug-prefix {
  padding: 0 10px;
  display: flex;
  align-items: center;
  font-size: 0.82rem;
  color: #94a3b8;
  background: #f1f5f9;
  border-right: 1.5px solid #e2e8f0;
  white-space: nowrap;
  user-select: none;
}

.slug-input {
  flex: 1;
  padding: 10px 12px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.92rem;
  color: #1e293b;
  font-family: inherit;
  min-width: 0;
}

.slug-input--error { color: #b91c1c; }
.slug-input--ok { color: #059669; }
.slug-input::placeholder { color: #94a3b8; }

.field-error {
  margin-top: 5px;
  font-size: 0.8rem;
  color: #b91c1c;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.field-ok {
  margin-top: 5px;
  font-size: 0.8rem;
  color: #059669;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
}

.field-hint {
  margin-top: 5px;
  font-size: 0.77rem;
  color: #94a3b8;
}

.setup-error {
  background: #fef2f2;
  border: 1.5px solid #fecaca;
  border-radius: 8px;
  padding: 10px 14px;
  color: #b91c1c;
  font-size: 0.87rem;
  font-weight: 500;
  margin-bottom: 1rem;
}

.btn-save {
  width: 100%;
  padding: 11px;
  background: linear-gradient(135deg, #243654 60%, #3a5a8c 100%);
  color: #fff;
  border: none;
  border-radius: 9px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: inherit;
}

.btn-save:hover:not([disabled]) {
  background: linear-gradient(135deg, #1a2840 60%, #2d4a7a 100%);
}

.btn-save[disabled] { opacity: 0.6; cursor: not-allowed; }

.setup-footer {
  margin-top: 1.25rem;
  display: flex;
  justify-content: center;
}

.btn-signout {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 0.83rem;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: color 0.15s, background 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: inherit;
}

.btn-signout:hover { color: #475569; background: #f1f5f9; }
</style>
